import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useUserStore } from 'store';
import { can } from '../../utils/access';
import { numFont } from '../../utils/fonts';
import { formatDate, formatDateOnly } from '../../utils';
import { formatPriceWithCurrency } from '../../utils/vat';
import ReasonDialog from './ReasonDialog';
import { BatchStatusChip } from './PaymentBatchesPage';
import { batchUrl, downloadBlobResponse, purchaseErrorMessage, toNumber } from './purchaseUtils';

function PaymentBatchDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentOrganization } = useUserStore();
  const canPay = can(currentOrganization, 'purchase_invoices.pay');
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const load = useCallback(async () => {
    try {
      const response = await axios.get(batchUrl(id));
      setBatch(response.data);
    } catch (error) {
      setSnackbar({ open: true, message: purchaseErrorMessage(t, error), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const downloadFile = async () => {
    setBusy('file');
    try {
      const response = await axios.get(batchUrl(id, 'file'), { responseType: 'blob' });
      downloadBlobResponse(response, `${batch?.number || 'maksuaineisto'}.xml`);
      notify(t('purchaseLedger.batches.fileDownloaded'));
      load();
    } catch (error) {
      // blob responses hide the JSON body; show the generic message
      notify(t('purchaseLedger.batches.fileFailed'), 'error');
    } finally {
      setBusy('');
    }
  };

  const confirm = async () => {
    setBusy('confirm');
    try {
      const response = await axios.post(batchUrl(id, 'confirm'), { payment_date: paymentDate || null });
      setBatch(response.data);
      setConfirmOpen(false);
      notify(t('purchaseLedger.batches.confirmed'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy('');
    }
  };

  const cancel = async (reason) => {
    setBusy('cancel');
    try {
      const response = await axios.post(batchUrl(id, 'cancel'), { reason });
      setBatch(response.data);
      setCancelOpen(false);
      notify(t('purchaseLedger.batches.cancelled_done'));
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy('');
    }
  };

  const removeItem = async (item) => {
    setBusy(`item-${item.id}`);
    try {
      const response = await axios.delete(`${batchUrl(id)}items/${item.id}/`);
      setBatch(response.data);
      if (response.data?.status === 'cancelled') navigate('/ostolaskut/maksuun');
    } catch (error) {
      notify(purchaseErrorMessage(t, error), 'error');
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!batch) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('purchaseLedger.batches.notFound')}</Alert>
      </Box>
    );
  }

  const open = batch.status === 'open' || batch.status === 'file_generated';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }} data-testid="payment-batch-detail">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ostolaskut/maksuun')} sx={{ mb: 1 }}>
        {t('purchaseLedger.batches.title')}
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h4" sx={{ ...numFont, fontWeight: 700 }} data-testid="pl-batch-number">
            {batch.number}
          </Typography>
          <BatchStatusChip status={batch.status} />
        </Box>
        {canPay && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {open && (
              <Button variant="contained" startIcon={busy === 'file' ? <CircularProgress size={16} /> : <DownloadIcon />} disabled={!!busy} onClick={downloadFile} data-testid="pl-batch-download">
                {t('purchaseLedger.batches.downloadFile')}
              </Button>
            )}
            {batch.status === 'file_generated' && (
              <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} disabled={!!busy} onClick={() => { setPaymentDate(batch.requested_execution_date || ''); setConfirmOpen(true); }} data-testid="pl-batch-confirm">
                {t('purchaseLedger.batches.confirm')}
              </Button>
            )}
            {open && (
              <Button variant="outlined" color="error" startIcon={<CancelIcon />} disabled={!!busy} onClick={() => setCancelOpen(true)} data-testid="pl-batch-cancel">
                {t('purchaseLedger.batches.cancel')}
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('purchaseLedger.batches.executionDate')}</Typography>
            <Typography sx={numFont}>{formatDateOnly(batch.requested_execution_date)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('purchaseLedger.fields.totalGross')}</Typography>
            <Typography sx={{ ...numFont, fontWeight: 700 }}>{formatPriceWithCurrency(toNumber(batch.total_amount))}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('purchaseLedger.batches.debtorAccount')}</Typography>
            <Typography sx={numFont}>{batch.debtor_iban} {batch.debtor_bic ? `/ ${batch.debtor_bic}` : ''}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">{t('purchaseLedger.batches.msgId')}</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontSize: 13 }}>{batch.msg_id}</Typography>
          </Box>
          {batch.file_generated_at && (
            <Box>
              <Typography variant="caption" color="text.secondary">{t('purchaseLedger.batches.fileGeneratedAt')}</Typography>
              <Typography sx={numFont}>{formatDate(batch.file_generated_at)}</Typography>
            </Box>
          )}
          {batch.confirmed_at && (
            <Box>
              <Typography variant="caption" color="text.secondary">{t('purchaseLedger.batches.confirmedAt')}</Typography>
              <Typography sx={numFont}>{formatDate(batch.confirmed_at)} {batch.confirmed_by_name ? `· ${batch.confirmed_by_name}` : ''}</Typography>
            </Box>
          )}
          {batch.cancel_reason && (
            <Box>
              <Typography variant="caption" color="text.secondary">{t('purchaseLedger.reason')}</Typography>
              <Typography>{batch.cancel_reason}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {batch.status === 'file_generated' && (
        <Alert severity="info" sx={{ mb: 2 }}>{t('purchaseLedger.batches.fileGeneratedHint')}</Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('purchaseLedger.fields.vendor')}</TableCell>
              <TableCell>{t('purchaseLedger.fields.invoiceNumber')}</TableCell>
              <TableCell>{t('purchaseLedger.fields.reference')}</TableCell>
              <TableCell align="right">{t('purchaseLedger.fields.totalGross')}</TableCell>
              {open && canPay && <TableCell padding="checkbox" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {(batch.items || []).map((item) => (
              <TableRow key={item.id} data-testid={`pl-batch-item-${item.id}`}>
                <TableCell>
                  <Typography component={RouterLink} to={`/ostolaskut/${item.purchase_invoice}`} variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
                    {item.invoice?.vendor_name || '-'}
                  </Typography>
                </TableCell>
                <TableCell sx={numFont}>{item.invoice?.invoice_number}</TableCell>
                <TableCell sx={numFont}>{item.remittance_reference || item.remittance_text}</TableCell>
                <TableCell align="right" sx={{ ...numFont, fontWeight: 600 }}>{formatPriceWithCurrency(toNumber(item.amount))}</TableCell>
                {open && canPay && (
                  <TableCell padding="checkbox">
                    <Tooltip title={t('purchaseLedger.batches.removeItem')}>
                      <IconButton size="small" disabled={!!busy} onClick={() => removeItem(item)} data-testid={`pl-batch-remove-${item.id}`}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth="xs" fullWidth data-testid="pl-batch-confirm-dialog">
        <DialogTitle>{t('purchaseLedger.batches.confirm')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('purchaseLedger.batches.confirmHelp')}</Typography>
          <TextField type="date" fullWidth size="small" label={t('purchaseLedger.payments.paymentDate')} value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ 'data-testid': 'pl-batch-confirm-date' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={!!busy}>{t('Cancel')}</Button>
          <Button variant="contained" color="success" onClick={confirm} disabled={!!busy} startIcon={busy === 'confirm' ? <CircularProgress size={16} /> : null} data-testid="pl-batch-confirm-submit">
            {t('purchaseLedger.batches.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <ReasonDialog open={cancelOpen} onClose={() => setCancelOpen(false)} onConfirm={cancel} title={t('purchaseLedger.batches.cancel')} description={t('purchaseLedger.batches.cancelHelp')} confirmLabel={t('purchaseLedger.batches.cancel')} required={false} busy={busy === 'cancel'} testId="pl-batch-cancel-dialog" />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PaymentBatchDetail;
